/**
 * Publish Blog Feature
 * 100% Isolated: Publish blog functionality that can be changed independently
 */
import { useState, useCallback } from 'react';
import { blogApiService } from '../api';
import { userService } from '../../user/service';
import { logger } from '@/shared/utils';

export const usePublishBlog = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publishBlog = useCallback(async (blogId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      logger.debug('Starting publish', 'usePublishBlog', { blogId });
      
      // Check authentication
      if (!userService.isAuthenticated()) {
        logger.warn('User not authenticated', 'usePublishBlog');
        throw new Error('Authentication required to publish a blog');
      }

      logger.debug('Calling publish API', 'usePublishBlog', { blogId });
      // Publish blog via API
      const response = await blogApiService.publishBlog(blogId);
      logger.debug('Publish API response received', 'usePublishBlog', { blogId, success: response.success });
      
      // Check if the response indicates success
      if (!response.success) {
        logger.error('Publish failed', 'usePublishBlog', { blogId, error: response.error });
        throw new Error(response.error?.message || 'Failed to publish blog');
      }
      
      logger.info('Blog published successfully', 'usePublishBlog', { blogId });
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to publish blog';
      setError(errorMessage);
      logger.error('Publish error', 'usePublishBlog', { blogId, error: err });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    publishBlog,
    loading,
    error,
    clearError: () => setError(null)
  };
};
